import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { RotateCcw } from 'lucide-react';
import { Select } from './form';
import {
  useFlows,
  useProjects,
  useSettings,
  type DashboardFilters as Filters,
} from '../api/queries';

type Ctx = {
  filters: Filters;
  setProjectId: (v: string) => void;
  setFlowId: (v: string) => void;
  setRiskThreshold: (v: number | undefined) => void;
  reset: () => void;
  /** ეფექტური ზღვარი — ან ხელით შეცვლილი, ან პარამეტრებიდან */
  effectiveThreshold: number;
};

const FiltersContext = createContext<Ctx | null>(null);

const STORAGE_KEY = 'doit.dashboard.filters';

/**
 * დეშბორდის ფილტრები საერთოა სამივე გვერდისთვის (მიმოხილვა / გაცდენები / ანალიტიკა),
 * ამიტომ context-შია და localStorage-ში ნარჩუნდება.
 */
export function DashboardFiltersProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useSettings();

  const [projectId, setProjectId] = useState('');
  const [flowId, setFlowId] = useState('');
  const [riskThreshold, setRiskThreshold] = useState<number | undefined>(undefined);

  // აღდგენა
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as Filters;
      if (saved.projectId) setProjectId(saved.projectId);
      if (saved.flowId) setFlowId(saved.flowId);
      if (typeof saved.riskThreshold === 'number') setRiskThreshold(saved.riskThreshold);
    } catch {
      /* გაფუჭებული ჩანაწერი — უბრალოდ ვიგნორებთ */
    }
  }, []);

  // შენახვა
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ projectId, flowId, riskThreshold }));
  }, [projectId, flowId, riskThreshold]);

  const value = useMemo<Ctx>(
    () => ({
      filters: { projectId, flowId, riskThreshold },
      setProjectId,
      setFlowId,
      setRiskThreshold,
      reset: () => {
        setProjectId('');
        setFlowId('');
        setRiskThreshold(undefined);
      },
      effectiveThreshold: riskThreshold ?? settings?.risk_threshold ?? 75,
    }),
    [projectId, flowId, riskThreshold, settings],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useDashboardFilters(): Ctx {
  const ctx = useContext(FiltersContext);
  if (!ctx) throw new Error('useDashboardFilters უნდა იყოს <DashboardFiltersProvider> შიგნით');
  return ctx;
}

// ============================================================
// ფილტრების ზოლი
// ============================================================

export function FiltersBar() {
  const { filters, setProjectId, setFlowId, setRiskThreshold, reset, effectiveThreshold } =
    useDashboardFilters();
  const { data: projects } = useProjects();
  // ნაკადების სია პროექტს მიჰყვება — არჩეული პროექტის გარეშე ყველა ჩანს
  const { data: flows } = useFlows(filters.projectId || undefined);
  const { data: settings } = useSettings();

  const isDefault =
    !filters.projectId && !filters.flowId && filters.riskThreshold === undefined;

  return (
    <div className="mb-5 flex flex-wrap items-center gap-3 rounded-[10px] border border-line bg-surface px-4 py-3 shadow-xs">
      <Select
        className="max-w-40"
        value={filters.projectId ?? ''}
        onChange={(e) => {
          setProjectId(e.target.value);
          // პროექტის შეცვლისას ძველი ნაკადი აღარ ერგება
          setFlowId('');
        }}
        placeholder="ყველა პროექტი"
        options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
      />

      <Select
        className="max-w-44"
        value={filters.flowId ?? ''}
        onChange={(e) => setFlowId(e.target.value)}
        placeholder="ყველა ნაკადი"
        options={(flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
      />

      <label className="flex items-center gap-2 text-[12px] text-ink-soft">
        რისკის ზღვარი &lt;
        <input
          type="number"
          min={0}
          max={100}
          value={effectiveThreshold}
          onChange={(e) => {
            const v = Number(e.target.value);
            setRiskThreshold(Number.isFinite(v) ? v : undefined);
          }}
          className="tabular w-16 rounded-md border border-line-strong bg-surface px-2 py-1 text-[13px] focus:border-brand-600 focus:outline-none"
        />
        %
        {filters.riskThreshold !== undefined &&
          settings &&
          filters.riskThreshold !== settings.risk_threshold && (
            <span className="text-[11px] text-warn">(ნაგულისხმევი {settings.risk_threshold}%)</span>
          )}
      </label>

      {!isDefault && (
        <button
          onClick={reset}
          className="ml-auto flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-ink-mute transition hover:bg-canvas hover:text-ink"
        >
          <RotateCcw className="size-3.5" />
          ფილტრების გასუფთავება
        </button>
      )}
    </div>
  );
}
